/**
 * THE STAR-TAKER'S CARD: one player in the room is offered a bet, and the match does not stop for
 * it either.
 *
 * A star opens an ordinary room-wide vote — HEADS or TAILS, the same ten seconds, everybody in
 * the room offered it — and the one player who drove over the star is offered something nobody
 * else is:
 *
 *  - **the auto-win**, which is *not answering*: they are counted in the winning majority and take
 *    one ordinary winner's share of the pot, guaranteed; or
 *  - **the gamble**, which is calling it: STAR_GAMBLE_PAYOUT_MULTIPLIER times their own current
 *    mass out of the pot if the room agrees with them, and **their run ends** if it does not.
 *
 * This is that card, and it is drawn instead of `./VoteCard.tsx` — never beside it — because they
 * are two readings of one question and a taker who answered on the ordinary ballot would be
 * gambling without having been told what a wrong call costs.
 *
 * Every rule `VoteCard` is under applies here unchanged, and the argument for each of them is in
 * that file's header: an `ArenaPanel` with `role="region"` and **no backdrop**, because nothing in
 * the simulation freezes for a vote and a modal would take the arena away on this side of the
 * wire; every control on **`pointerup`** through `./tap-activate.ts`, because a thumb already on
 * the joystick means the platform reports no tap for a second finger and this card has a
 * ten-second deadline on it; **nothing predicted**, so a pressed call says SENT and never done;
 * and **the first answer wins**, so the buttons go inert the moment one is in.
 *
 * The three rules that are this card's own:
 *
 * - **IT PRINTS THE ACTUAL NUMBER.** Three times your own mass is arithmetic nobody should be
 *   doing while being chased, so `starGamblePayout` does it off the mass the HUD is already
 *   holding and the card draws the figure. It is drawn as an upper bound — "or the pot, if the
 *   pot is smaller" — because the payout is capped at what the pot actually holds and a promise
 *   would be the one number here that could be a lie. See `./star-choice.ts`.
 * - **IT SAYS DEATH OUT LOUD.** A wrong call ends the run. It is the only control in this HUD
 *   that can, so it is the only one that carries the word.
 * - **THE SAFE OPTION IS THE ABSENCE OF A PRESS, AND IT STILL GETS A BUTTON.** The auto-win is
 *   *not answering* — there is no "skip" on the wire and there must not be one — so "Take the
 *   share" sends nothing at all and does exactly what the ✕ does: it closes the card. It exists
 *   because a decision somebody makes deliberately should be a thing they can press, rather than
 *   only a thing they can fail to do; and because the safe option being the default is what makes
 *   a dropped frame, a duel or a distraction land on the outcome that cannot kill anybody.
 *
 * **IT HAS A SECOND MOUNT SITE, AND KEEPING IT ABLE TO HAVE ONE IS A PROPERTY WORTH NAMING.**
 * `./DuelPotBar.tsx`' and `./MiniGameChoiceIcon.tsx`' bargain: this takes a `VoteView` and a mass
 * and nothing else — no connection, no wire, no store beyond the overlay settings — so the
 * instructions page mounts *this* card against a sample rather than redrawing it, and a player
 * meets the decision with the clock stopped before they ever meet it with ten seconds on it. Any
 * future prop this grows should stay something a caller can simply hand over.
 */

import { useState } from 'react';
import type { VoteOption } from '@chompdome/shared/types';
import type { VoteView } from '../net/connection';
import { ArenaPanel } from './ArenaPanel';
import { useHudSettings } from './HudSettings';
import { useTapActivate } from './tap-activate';
import { starGamblePayout } from './star-choice';
import { formatVoteMass } from './vote-card';
import { VoteCountdown, VoteSeconds } from './VoteCountdown';

export interface StarChoiceCardProps {
  /** An open vote whose `star` is true — `Hud` is what branches on that. */
  vote: VoteView;
  /**
   * The taker's own mass right now, off `HudSnapshot.mass`.
   *
   * The HUD's own number rather than a second read of the world: it is the same figure the run
   * panel is drawing a few pixels away, so the two cannot disagree about what is being risked.
   * The room settles the payout against the mass it holds at resolution, which is why the card
   * says "about" — a player who has eaten three pellets since reading this is worth a little more
   * than the figure they read.
   */
  mass: number;
  /** Call it, as an option **id**. Refused by the connection once we have already answered. */
  onAnswer: (optionId: string) => void;
  compact?: boolean;
  /** Injected so the countdown is deterministic under test. */
  now?: () => number;
}

export function StarChoiceCard({
  vote,
  mass,
  onAnswer,
  compact = false,
  now = Date.now,
}: StarChoiceCardProps) {
  // Dismissed by the player and by nothing else — `VoteCard`'s field, and here it is also the
  // auto-win: closing this card is choosing not to call it.
  const [dismissed, setDismissed] = useState(false);
  // Battery saver may settle the card's movement and may never take a control, the stakes or the
  // countdown away: fidelity, never visibility.
  const { batterySaver } = useHudSettings();

  const answered = vote.yourChoice !== null;
  const payout = starGamblePayout(mass);

  return (
    <ArenaPanel
      open={!dismissed}
      onClose={() => setDismissed(true)}
      title="Your star"
      testId="star-choice"
      className={batterySaver ? 'w-full' : 'chompdome-vote-card w-full'}
    >
      <p
        data-testid="star-prompt"
        className={`font-semibold leading-snug text-foreground ${compact ? 'text-[11px]' : 'text-xs'}`}
      >
        {vote.prompt}
      </p>

      {/*
       * THE STAKES, BEFORE THE CONTROLS. The decision is the feature, so what each side is worth
       * is read before either button is reachable rather than as a caption under one of them.
       */}
      <dl
        data-testid="star-stakes"
        className={`mt-2 flex flex-col gap-1 ${compact ? 'text-[10px]' : 'text-[11px]'}`}
      >
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Call it right</dt>
          <dd data-testid="star-payout" className="font-bold tabular-nums text-primary">
            about {formatVoteMass(payout)} mass
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Call it wrong</dt>
          <dd data-testid="star-forfeit" className="font-black uppercase text-destructive">
            You die
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-muted-foreground">Don’t call it</dt>
          <dd className="font-bold text-foreground">A winner’s share</dd>
        </div>
      </dl>

      <p
        data-testid="star-cap"
        className={`mt-1 leading-snug text-muted-foreground ${compact ? 'text-[9px]' : 'text-[10px]'}`}
      >
        {/* An upper bound and never a promise: the payout is capped at what the pot actually
            holds, and mass never comes out of nothing. */}
        Three times your mass, or the whole pot if the pot is smaller.
      </p>

      <div
        className="mt-2 grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${vote.options.length}, 1fr)` }}
      >
        {vote.options.map((option) => (
          <StarCallButton
            key={option.id}
            option={option}
            picked={vote.yourChoice === option.id}
            // The first answer wins in the room, so a second control here would be one that does
            // nothing — `VoteCard`'s rule, and here the press it would be refusing is the one
            // that stakes the run.
            locked={answered || !vote.canAnswer}
            // SENT, NEVER DONE. There is no acknowledgement on the wire for an open vote's
            // answer, so `yourChoice` is our own tap and `answerSent` is the frame that carried
            // it. Neither is the room agreeing, and this is the card where that matters most.
            note={vote.yourChoice !== option.id ? null : vote.answerSent ? 'Sent' : 'Sending…'}
            compact={compact}
            onAnswer={onAnswer}
          />
        ))}
      </div>

      {/*
       * THE SAFE OPTION, AND IT SENDS NOTHING. Pressing it closes the card, which is exactly what
       * the ✕ does and exactly what happens to a taker who never presses anything: the auto-win
       * is the *absence* of an answer, so there is nothing for this to put on the wire.
       */}
      {!answered && <TakeTheShareButton compact={compact} onTake={() => setDismissed(true)} />}

      <div className="mt-2 flex items-center gap-2">
        <VoteCountdown endsAtMs={vote.endsAtMs} now={now} still={batterySaver} />
        <VoteSeconds endsAtMs={vote.endsAtMs} now={now} />
      </div>

      <p
        data-testid="star-status"
        role="status"
        className={`mt-1 leading-snug text-muted-foreground ${compact ? 'text-[10px]' : 'text-[11px]'}`}
      >
        {statusLine(vote)}
      </p>
    </ArenaPanel>
  );
}

/**
 * The one line under the controls.
 *
 * A function rather than nested ternaries in the markup — `VoteCard.statusLine`'s and
 * `DuelOverlay.statusLine`'s arrangement — because the two cases say genuinely different things,
 * and the second of them has to be honest about what is now out of the player's hands.
 */
function statusLine(vote: VoteView): string {
  if (vote.yourChoice !== null) {
    return 'Your call is in. The room settles it when time is up.';
  }
  return 'Say nothing and the share is yours. There is no coin — the room decides.';
}

/**
 * One side of the call.
 *
 * **ITS OWN COMPONENT SO IT CAN TAKE ITS PRESS ON `pointerup`** — `useTapActivate` is a hook and
 * cannot be called inside a `map`. `VoteCard.VoteAnswerButton`'s shape exactly and for its
 * reason, with more riding on it than anywhere else in this HUD: a thumb on the joystick means
 * the platform reports no tap for a second contact, and here that would run the deadline out
 * under somebody pressing the button that stakes their run.
 */
function StarCallButton({
  option,
  picked,
  locked,
  note,
  compact,
  onAnswer,
}: {
  option: VoteOption;
  picked: boolean;
  locked: boolean;
  note: string | null;
  compact: boolean;
  onAnswer: (optionId: string) => void;
}) {
  const activate = useTapActivate(() => onAnswer(option.id), locked);
  return (
    <button
      type="button"
      disabled={locked}
      {...activate}
      aria-pressed={picked}
      data-testid="star-call"
      className={`flex touch-manipulation select-none flex-col items-center justify-center gap-0.5 rounded-lg border-2 px-1 transition-colors disabled:cursor-not-allowed ${
        compact ? 'min-h-[2rem] py-1' : 'min-h-[2.5rem] py-1.5'
      } ${
        picked
          ? 'border-destructive bg-destructive/15 text-destructive'
          : 'border-destructive/60 bg-background/60 hover:border-destructive disabled:opacity-40'
      }`}
    >
      <span className={`font-black leading-tight ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
        {option.label}
      </span>
      {note === null ? null : (
        <span
          data-testid="star-call-note"
          className="text-[9px] font-bold uppercase tracking-widest opacity-80"
        >
          {note}
        </span>
      )}
    </button>
  );
}

/** Take the guaranteed share: closes the card, sends nothing, and says so. */
function TakeTheShareButton({ compact, onTake }: { compact: boolean; onTake: () => void }) {
  const activate = useTapActivate(onTake);
  return (
    <button
      type="button"
      {...activate}
      data-testid="star-take-share"
      className={`mt-1.5 w-full touch-manipulation select-none rounded-lg border border-border bg-background/60 font-semibold transition-colors hover:border-primary/60 ${
        compact ? 'min-h-[1.75rem] py-1 text-[10px]' : 'min-h-[2rem] py-1.5 text-[11px]'
      }`}
    >
      Take the share
    </button>
  );
}
